/**
 * useSidebarMessages - Simplified React Query hook for sidebar chat
 *
 * Features:
 * - Simple pagination with infinite scroll
 * - Send messages via API
 * - Auto-refetch on WebSocket events (handled by React Query)
 */

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback, useRef, useEffect } from 'react';
import { listSidebarMessages } from '../services/messageService';
import { messageQueryKeys } from '../config/queryKeys';
import { getTenantDetails } from 'utils/utils';
import { useSendSidebarMessage } from './useMessageMutations';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { useWebSocket } from 'context/WebSocketConnectionContext';

/**
 * Fetch and manage sidebar messages
 * @param {string} caseId - Case ID
 * @param {string} messageThreadId - Thread ID
 * @param {Object} messageDetails - Full message details (for sending)
 * @returns Hook state and methods
 */
export function useSidebarMessages(caseId, messageThreadId) {
  const [tenantId] = getTenantDetails();
  const [errorMessage, setErrorMessage] = useState(null);
  const scrollContainerRef = useRef(null);
  const queryClient = useQueryClient();
  const { userDetails, firmDetails } = useUserDetailsContext() || {};
  const { subscribe } = useWebSocket();

  const sendMutation = useSendSidebarMessage();

  // Fetch sidebar messages with infinite scroll
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } = useInfiniteQuery({
    queryKey: messageQueryKeys.sidebar.byThread(messageThreadId),
    queryFn: ({ pageParam = 0 }) =>
      listSidebarMessages({
        message_thread_id: messageThreadId,
        limit: 20,
        offset: pageParam,
      }),
    enabled: Boolean(messageThreadId),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage, allPages) => {
      const totalCount = lastPage?.data?.count || 0;
      const loadedCount = allPages.reduce((sum, page) => sum + (page?.data?.sidebar_messages?.length || 0), 0);

      return loadedCount < totalCount ? loadedCount : undefined;
    },
    onError: (err) => {
      if (err?.response?.status === 403) {
        setErrorMessage(err?.response?.data || 'Access denied');
      }
    },
  });

  // Flatten all messages (oldest first)
  const messages = data?.pages.flatMap((page) => page?.data?.sidebar_messages || []).reverse() || [];
  const totalCount = data?.pages?.[0]?.data?.count || 0;

  // Send message with optimistic cache update
  const sendMessage = useCallback(
    async (messageText) => {
      if (!messageText?.trim()) return;

      const trimmedMessage = messageText.trim();
      const queryKey = messageQueryKeys.sidebar.byThread(messageThreadId);

      const optimisticMessage = {
        sk: `optimistic-${Date.now()}`,
        message: trimmedMessage,
        created_at: new Date().toISOString(),
        author_details: {
          firm_user_id: firmDetails?.firm_user_id || userDetails?.firm_user_id,
          first_name: userDetails?.first_name,
          last_name: userDetails?.last_name,
        },
        _optimistic: true,
      };

      // Optimistically inject the message into the cache (page 0, newest-first)
      queryClient.setQueryData(queryKey, (oldData) => {
        if (!oldData?.pages?.length) return oldData;
        const updatedPages = [...oldData.pages];
        const firstPage = { ...updatedPages[0] };
        firstPage.data = {
          ...firstPage.data,
          count: (firstPage.data?.count || 0) + 1,
          sidebar_messages: [optimisticMessage, ...(firstPage.data?.sidebar_messages || [])],
        };
        updatedPages[0] = firstPage;
        return { ...oldData, pages: updatedPages };
      });

      const payload = {
        type: 'sidebar',
        tenant_id: tenantId,
        message_thread_id: messageThreadId,
        case_id: caseId,
        message: trimmedMessage,
      };

      try {
        await sendMutation.mutateAsync(payload).then((response) => {
          // Confirm: remove the optimistic flag so the message displays normally
          queryClient.setQueryData(queryKey, (oldData) => {
            if (!oldData?.pages?.length) return oldData;
            const updatedPages = [...oldData.pages];
            const firstPage = { ...updatedPages[0] };
            firstPage.data = {
              ...firstPage.data,
              sidebar_messages: (firstPage.data?.sidebar_messages || []).map((m) =>
                m.sk === optimisticMessage.sk ? { ...m, _optimistic: false, sidebar_message_id: response?.data?.sidebar_message_id } : m
              ),
            };
            updatedPages[0] = firstPage;
            return { ...oldData, pages: updatedPages };
          });
        });
      } catch {
        // Rollback: remove the optimistic message on failure
        queryClient.setQueryData(queryKey, (oldData) => {
          if (!oldData?.pages?.length) return oldData;
          const updatedPages = [...oldData.pages];
          const firstPage = { ...updatedPages[0] };
          firstPage.data = {
            ...firstPage.data,
            count: Math.max((firstPage.data?.count || 1) - 1, 0),
            sidebar_messages: (firstPage.data?.sidebar_messages || []).filter((m) => m.sk !== optimisticMessage.sk),
          };
          updatedPages[0] = firstPage;
          return { ...oldData, pages: updatedPages };
        });
        throw new Error('Failed to send message');
      }
    },
    [tenantId, messageThreadId, caseId, sendMutation, firmDetails, userDetails, queryClient]
  );

  // WebSocket: listen for incoming sidebar messages and inject into cache directly (no API call)
  useEffect(() => {
    if (!caseId || !messageThreadId || !subscribe) return;

    const queryKey = messageQueryKeys.sidebar.byThread(messageThreadId);

    const currentUserId = firmDetails?.client_cognito_username;

    const handleWsSidebarMessage = (wsData) => {
      // Filter to current thread only
      if (wsData?.message_thread_id !== messageThreadId) return;

      queryClient.setQueryData(queryKey, (oldData) => {
        if (!oldData?.pages?.length) return oldData;

        const isFromCurrentUser =
          (wsData?.created_by_user_id && wsData.created_by_user_id === currentUserId) ||
          (wsData?.user_id && wsData.user_id === currentUserId);

        if (isFromCurrentUser) {
          return oldData;
        }

        // Shape WS payload to match the API message format expected by SidebarMessage
        const incomingMessage = {
          sk: wsData.sk,
          sidebar_message_id: wsData.sidebar_message_id,
          message: wsData.message,
          created_at: wsData.created_at,
          author_details: {
            firm_user_id: wsData.created_by_user_id,
            contact_id: wsData.contact_id,
            first_name: wsData.name?.first_name || wsData.created_by_name?.first_name,
            last_name: wsData.name?.last_name || wsData.created_by_name?.last_name,
          },
          created_by_user_image: wsData.created_by_user_image,
        };

        // Prepend to page 0 (newest-first storage); the .reverse() in selector surfaces it at the bottom
        const updatedPages = [...oldData.pages];
        const firstPage = { ...updatedPages[0] };
        firstPage.data = {
          ...firstPage.data,
          count: (firstPage.data?.count || 0) + 1,
          sidebar_messages: [incomingMessage, ...(firstPage.data?.sidebar_messages || [])],
        };
        updatedPages[0] = firstPage;
        return { ...oldData, pages: updatedPages };
      });
    };

    const unsubscribe = subscribe('sidebar_message', handleWsSidebarMessage);

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [caseId, messageThreadId, firmDetails?.firm_user_id]);

  // Load more (older messages)
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    messages,
    totalCount,
    isLoading,
    isSending: sendMutation.isPending,
    hasMore: hasNextPage,
    isLoadingMore: isFetchingNextPage,
    errorMessage,
    scrollContainerRef,
    sendMessage,
    loadMore,
    refetch,
  };
}
